import React, { useEffect, useState } from 'react';
import AdminModal from '../components/AdminModal';
import {
  deleteBrand,
  deleteCategory,
  fetchBrands,
  fetchCategories,
  reorderBrands,
  reorderCategories,
  saveBrand,
  saveCategory,
  slugify,
} from '../lib/adminApi';

function EntityRow({ item, index, onEdit, onDelete, onDragStart, onDrop, metaPrefix = '/' }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={e => e.preventDefault()}
      onDrop={onDrop}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 14,
        alignItems: 'center',
        padding: 12,
        border: '1px solid #eee',
        borderRadius: 14,
        cursor: 'grab',
        background: '#fff',
      }}
    >
      <div>
        <div style={{ fontWeight: 600 }}>{item.name}</div>
        <div style={{ color: '#777', fontSize: 13 }}>
          {metaPrefix}
          {item.slug} · {item.visible !== false ? 'Visible' : 'Hidden'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="admin-btn admin-btn-secondary admin-btn-sm"
          onClick={() => onEdit(item)}
        >
          Edit
        </button>
        <button
          type="button"
          className="admin-btn admin-btn-danger admin-btn-sm"
          onClick={() => onDelete(item)}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function Categories() {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingSport, setEditingSport] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [sportDragIndex, setSportDragIndex] = useState(null);
  const [brandDragIndex, setBrandDragIndex] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [categoryData, brandData] = await Promise.all([
        fetchCategories(),
        fetchBrands(),
      ]);
      setCategories(categoryData);
      setBrands(brandData);
    } catch (err) {
      setError(err.message || 'Could not load data. Run supabase-admin.sql and supabase-brands.sql.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNewSport = () => {
    setEditingSport({
      name: '',
      slug: '',
      description: '',
      image_url: '',
      featured_image_url: '',
      visible: true,
      display_order: categories.length + 1,
    });
  };

  const openNewBrand = () => {
    setEditingBrand({
      name: '',
      slug: '',
      description: '',
      visible: true,
      display_order: brands.length + 1,
    });
  };

  const handleSaveSport = async event => {
    event.preventDefault();
    if (!editingSport.name.trim()) {
      setError('Sport name is required.');
      return;
    }

    try {
      const payload = {
        ...editingSport,
        name: editingSport.name.trim(),
        slug: editingSport.slug.trim() || slugify(editingSport.name),
      };
      await saveCategory(payload);
      setEditingSport(null);
      setSuccess('Sport saved.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not save sport.');
    }
  };

  const handleSaveBrand = async event => {
    event.preventDefault();
    if (!editingBrand.name.trim()) {
      setError('Brand name is required.');
      return;
    }

    try {
      const payload = {
        ...editingBrand,
        name: editingBrand.name.trim(),
        slug: editingBrand.slug.trim() || slugify(editingBrand.name),
      };
      await saveBrand(payload);
      setEditingBrand(null);
      setSuccess('Brand saved.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not save brand.');
    }
  };

  const handleDeleteSport = async category => {
    if (!window.confirm(`Delete sport "${category.name}"?`)) return;
    try {
      await deleteCategory(category.id);
      setSuccess('Sport deleted.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not delete sport.');
    }
  };

  const handleDeleteBrand = async brand => {
    if (!window.confirm(`Delete brand "${brand.name}"?`)) return;
    try {
      await deleteBrand(brand.id);
      setSuccess('Brand deleted.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not delete brand.');
    }
  };

  const onSportDropAt = async index => {
    if (sportDragIndex === null || sportDragIndex === index) {
      setSportDragIndex(null);
      return;
    }
    const next = [...categories];
    const [moved] = next.splice(sportDragIndex, 1);
    next.splice(index, 0, moved);
    setCategories(next);
    setSportDragIndex(null);
    try {
      await reorderCategories(next);
      setSuccess('Sport order updated.');
    } catch (err) {
      setError(err.message || 'Could not reorder sports.');
      load();
    }
  };

  const onBrandDropAt = async index => {
    if (brandDragIndex === null || brandDragIndex === index) {
      setBrandDragIndex(null);
      return;
    }
    const next = [...brands];
    const [moved] = next.splice(brandDragIndex, 1);
    next.splice(index, 0, moved);
    setBrands(next);
    setBrandDragIndex(null);
    try {
      await reorderBrands(next);
      setSuccess('Brand order updated.');
    } catch (err) {
      setError(err.message || 'Could not reorder brands.');
      load();
    }
  };

  const uploadSportField = async (field, file) => {
    if (!file) return;
    try {
      const { uploadProductImage } = await import('../lib/adminApi');
      const url = await uploadProductImage(file, 'categories');
      setEditingSport(current => ({ ...current, [field]: url }));
    } catch (err) {
      setError(err.message || 'Upload failed.');
    }
  };

  return (
    <div>
      <div className="admin-topbar" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="admin-page-title">Sports and Brands</h1>
          <p className="admin-page-subtitle">
            Manage sports for navigation and licensed brands for footer / brand pages
          </p>
        </div>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {success && <p className="admin-success-msg">{success}</p>}

      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="admin-topbar" style={{ marginBottom: 16 }}>
          <div>
            <h2 className="admin-card-title" style={{ margin: 0 }}>Sports</h2>
            <p style={{ margin: '6px 0 0', color: '#777', fontSize: 14 }}>
              Shown in top navigation and sport landing pages
            </p>
          </div>
          <button type="button" className="admin-btn admin-btn-primary" onClick={openNewSport}>
            Add sport
          </button>
        </div>

        {loading && <p className="admin-empty">Loading…</p>}
        {!loading && (
          <div style={{ display: 'grid', gap: 10 }}>
            {categories.map((category, index) => (
              <EntityRow
                key={category.id}
                item={category}
                index={index}
                onEdit={setEditingSport}
                onDelete={handleDeleteSport}
                onDragStart={() => setSportDragIndex(index)}
                onDrop={() => onSportDropAt(index)}
              />
            ))}
            {!categories.length && <p className="admin-empty">No sports yet.</p>}
          </div>
        )}
      </div>

      <div className="admin-card">
        <div className="admin-topbar" style={{ marginBottom: 16 }}>
          <div>
            <h2 className="admin-card-title" style={{ margin: 0 }}>Brands</h2>
            <p style={{ margin: '6px 0 0', color: '#777', fontSize: 14 }}>
              Licensed brands — footer links and /brands pages only. Not the same as team.
            </p>
          </div>
          <button type="button" className="admin-btn admin-btn-primary" onClick={openNewBrand}>
            Add brand
          </button>
        </div>

        {loading && <p className="admin-empty">Loading…</p>}
        {!loading && (
          <div style={{ display: 'grid', gap: 10 }}>
            {brands.map((brand, index) => (
              <EntityRow
                key={brand.id}
                item={brand}
                index={index}
                metaPrefix="/brands/"
                onEdit={setEditingBrand}
                onDelete={handleDeleteBrand}
                onDragStart={() => setBrandDragIndex(index)}
                onDrop={() => onBrandDropAt(index)}
              />
            ))}
            {!brands.length && <p className="admin-empty">No brands yet. Add Mercedes, Puma, etc.</p>}
          </div>
        )}
      </div>

      {editingSport && (
        <AdminModal
          title={editingSport.id ? 'Edit sport' : 'Add sport'}
          onClose={() => setEditingSport(null)}
        >
          <form onSubmit={handleSaveSport}>
            <div className="admin-field">
              <label className="admin-label">Sport name</label>
              <input
                className="admin-input"
                value={editingSport.name}
                onChange={e =>
                  setEditingSport(c => ({
                    ...c,
                    name: e.target.value,
                    slug: c.id ? c.slug : slugify(e.target.value),
                  }))
                }
                required
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Slug</label>
              <input
                className="admin-input"
                value={editingSport.slug}
                onChange={e => setEditingSport(c => ({ ...c, slug: slugify(e.target.value) }))}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Description</label>
              <textarea
                className="admin-textarea"
                value={editingSport.description || ''}
                onChange={e => setEditingSport(c => ({ ...c, description: e.target.value }))}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={e => uploadSportField('image_url', e.target.files?.[0])}
              />
              {editingSport.image_url && (
                <img src={editingSport.image_url} alt="" className="admin-thumb" style={{ marginTop: 8 }} />
              )}
            </div>
            <label className="admin-checkbox" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={editingSport.visible !== false}
                onChange={e => setEditingSport(c => ({ ...c, visible: e.target.checked }))}
              />
              Visible
            </label>
            <button type="submit" className="admin-btn admin-btn-primary" style={{ width: '100%' }}>
              Save sport
            </button>
          </form>
        </AdminModal>
      )}

      {editingBrand && (
        <AdminModal
          title={editingBrand.id ? 'Edit brand' : 'Add brand'}
          onClose={() => setEditingBrand(null)}
        >
          <form onSubmit={handleSaveBrand}>
            <div className="admin-field">
              <label className="admin-label">Brand name</label>
              <input
                className="admin-input"
                value={editingBrand.name}
                onChange={e =>
                  setEditingBrand(c => ({
                    ...c,
                    name: e.target.value,
                    slug: c.id ? c.slug : slugify(e.target.value),
                  }))
                }
                placeholder="e.g. Mercedes, Puma, Adidas"
                required
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Slug</label>
              <input
                className="admin-input"
                value={editingBrand.slug}
                onChange={e => setEditingBrand(c => ({ ...c, slug: slugify(e.target.value) }))}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Description (optional)</label>
              <textarea
                className="admin-textarea"
                value={editingBrand.description || ''}
                onChange={e => setEditingBrand(c => ({ ...c, description: e.target.value }))}
              />
            </div>
            <p style={{ margin: '0 0 16px', color: '#777', fontSize: 13 }}>
              Upload the brand page header in Settings → Collection headers after saving.
              Assign this brand on each product in the product editor.
            </p>
            <label className="admin-checkbox" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={editingBrand.visible !== false}
                onChange={e => setEditingBrand(c => ({ ...c, visible: e.target.checked }))}
              />
              Visible
            </label>
            <button type="submit" className="admin-btn admin-btn-primary" style={{ width: '100%' }}>
              Save brand
            </button>
          </form>
        </AdminModal>
      )}
    </div>
  );
}

export default Categories;
